import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import faceIdIcon from '~/assets/dashboard/pointage-face-id.svg'
import checkBadgeIcon from '~/assets/dashboard/pointage-check-badge.svg'
import { Camera01Icon } from './icons/dashboard-icons'

export interface PointageCameraHandle {
  takePhoto: () => Promise<Blob | null>
}

/**
 * Caméra témoin du pointage kiosk (Figma 11393:41501). Flux webcam via `getUserMedia`, démarré
 * automatiquement au montage (l'étape 2 est atteinte après un clic utilisateur, donc dans un
 * contexte de geste valide pour le navigateur ; requiert HTTPS ou localhost). La zone est cliquable
 * pour figer la photo témoin : le parent appelle `takePhoto()` via la ref puis passe `captured` à
 * `true`, ce qui remplace l'icône Face-ID par la pastille « Identité validée ».
 */
export function PointageCamera({
  captured = false,
  onCapture,
  ref,
}: {
  captured?: boolean
  onCapture?: () => void
  ref?: React.Ref<PointageCameraHandle>
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [active, setActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasCamera = !!navigator.mediaDevices?.getUserMedia

  const start = async () => {
    if (!hasCamera) {
      setError('Caméra indisponible sur cet appareil')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'user' }, aspectRatio: 1 },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      setError(null)
      setActive(true)
    } catch {
      setError("Impossible d'accéder à la caméra")
    }
  }

  // Démarrage auto au montage + arrêt du flux au démontage (retour étape 1 = démontage du composant).
  useEffect(() => {
    void start()
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useImperativeHandle(ref, () => ({
    takePhoto: () =>
      new Promise<Blob | null>((resolve) => {
        const video = videoRef.current
        const canvas = canvasRef.current
        if (!video || !canvas || !video.videoWidth) return resolve(null)
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(null)
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9)
      }),
  }))

  const clickable = active && !captured

  const renderCenter = () => {
    if (captured) {
      return (
        <span className="relative flex items-center gap-[16px] rounded-[16px] bg-[#b8d9a8] p-[16px]">
          <span className="flex size-[48px] shrink-0 items-center justify-center rounded-[8px] bg-[#a4c494]">
            <img src={checkBadgeIcon} alt="" aria-hidden className="size-[24px]" />
          </span>
          <span className="font-brand text-[18px] leading-[20px] font-semibold text-[#354a31]">
            Identité validée
          </span>
        </span>
      )
    }
    if (active) {
      return <img src={faceIdIcon} alt="" aria-hidden className="relative size-[64px]" />
    }
    return (
      <span className="relative flex max-w-[320px] flex-col items-center gap-[12px] px-[20px] text-center">
        <Camera01Icon className="size-[40px] text-[#86ab7d]" />
        <span className="font-sans text-[13px] text-white/80">
          {error ?? 'Autorisation de la caméra en cours…'}
        </span>
        {error && (
          <button
            type="button"
            onClick={start}
            className="cursor-pointer rounded-full bg-[#6a9560] px-[20px] py-[8px] font-sans text-[13px] font-semibold text-[#f9f8f1]"
          >
            Réessayer
          </button>
        )}
      </span>
    )
  }

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onCapture : undefined}
      className={`relative flex h-[440px] w-full items-center justify-center overflow-hidden rounded-[12px] bg-[#2f3d2a] ${clickable ? 'cursor-pointer' : ''}`}
      aria-label={clickable ? 'Prendre la photo témoin' : 'Vérification de votre identité'}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={active ? 'size-full -scale-x-100 object-cover' : 'hidden'}
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Ovales de scan (Figma : ovale pointillé + fin ovale plein, coins asymétriques). */}
      <span className="pointer-events-none absolute h-[400px] w-[320px] rounded-t-[160px] rounded-b-[235px] border-[3px] border-dashed border-[#86ab7d]" />
      <span className="pointer-events-none absolute h-[390px] w-[312px] rounded-t-[156px] rounded-b-[229px] border border-[#86ab7d]" />

      {renderCenter()}
    </div>
  )
}
